import axiosInstance from '../api/axios';
import type { GetTradersParams, TraderGroupReferralReportParams, TraderLookup } from '@/types/trader';

const DEFAULT_LIST_LIMIT = 300;

interface TraderBalancesHistoryExportParams {
  format: 'csv' | 'xlsx';
  trader_id?: string | null;
  currency_id?: string | null;
  operation_type?: string | null;
  transaction_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

interface TraderBalancesHistoryParams {
  trader_id?: string | null;
  currency_id?: string | null;
  operation_type?: string | null;
  transaction_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  limit?: number;
  offset?: number;
}

interface TraderGroupReferralReportExportParams extends TraderGroupReferralReportParams {
  format: 'csv' | 'xlsx';
}

export interface PenaltyTraderOption {
  id: string;
  name: string | null;
  username: string | null;
}

export interface PenaltyBalanceOption {
  id: string;
  currency_id: string | null;
  currency_iso_code: string | null;
  available_balance: string;
}

export type PenaltyReason =
  | 'one_time_sla_violation'
  | 'no_response_to_appeal'
  | 'late_supporting_evidence'
  | 'repeated_sla_violations'
  | 'outdated_payment_details'
  | 'communication_rules_breach'
  | 'security_deposit_below_minimum'
  | 'confirmed_fraud'
  | 'other';

export const tradersService = {
  getPenaltyHistory: async (params: { trader_id?: string; from_date?: string; to_date?: string; limit: number; offset: number }) =>
    (await axiosInstance.get('/traders/penalties/history', { params })).data,
  getUserOptions: async (): Promise<{ id: string; username: string }[]> => {
    const response = await axiosInstance.get('/traders/user-options', { params: { limit: 1000 } });
    return response.data;
  },
  searchPenaltyTraders: async (search = '', limit = 30) => {
    const response = await axiosInstance.get<{ items: PenaltyTraderOption[]; total: number }>(
      '/traders/penalties/traders',
      { params: { search, limit } },
    );
    return response.data;
  },

  getPenaltyTraderBalances: async (traderId: string) => {
    const response = await axiosInstance.get<PenaltyBalanceOption[]>(
      `/traders/penalties/traders/${traderId}/balances`,
    );
    return response.data;
  },

  createTraderPenalty: async (payload: {
    penalty_id: string;
    transaction_id?: string;
    trader_id: string;
    balance_id: string;
    amount: string;
    reason: PenaltyReason;
    comment?: string;
  }) => {
    const response = await axiosInstance.post('/traders/penalties', payload);
    return response.data;
  },

  searchTraders: async (search = '', limit = 30) => {
    const response = await axiosInstance.get<TraderLookup[]>('/traders/search', {
      params: { search, limit },
    });
    return response.data;
  },

  getAuthSessionsSummary: async () => {
    const response = await axiosInstance.get('/traders/auth-sessions/summary');
    return response.data;
  },

  getAuthSessions: async (params: {
    search?: string;
    session_status?: 'all' | 'active' | 'expired' | 'revoked';
    limit?: number;
    offset?: number;
  } = {}) => {
    const response = await axiosInstance.get('/traders/auth-sessions', { params });
    return response.data;
  },

  getTraders: async (params: GetTradersParams = {}) => {
    const response = await axiosInstance.get('/traders', {
      params: {
        limit: DEFAULT_LIST_LIMIT,
        ...params,
      },
    });
    return response.data;
  },

  getTrader: async (id: string) => {
    const response = await axiosInstance.get(`/traders/${id}`);
    return response.data;
  },

  getTelegramChats: async (id: string) => {
    const response = await axiosInstance.get(`/traders/${id}/telegram-chats`);
    return response.data;
  },
  updateTelegramChatLink: async (traderId: string, chatId: string, inviteUrl: string | null) =>
    (await axiosInstance.patch(`/traders/${traderId}/telegram-chats/${chatId}`, { invite_url: inviteUrl })).data,
  createTelegramChat: async (traderId: string, data: { chat_kind: string; title: string; invite_url: string }) =>
    (await axiosInstance.post(`/traders/${traderId}/telegram-chats`, data)).data,
  updateTelegramChat: async (traderId: string, chatId: string, data: { chat_kind: string; title: string; invite_url: string }) =>
    (await axiosInstance.put(`/traders/${traderId}/telegram-chats/${chatId}`, data)).data,

  resetTrader2fa: async (id: string) => {
    const response = await axiosInstance.delete(`/traders/${id}/2fa`);
    return response.data;
  },
  getTraderGroupMembers: async (
    groupId: string,
    params: Pick<TraderGroupReferralReportParams, 'start_date' | 'end_date'> = {},
  ) => {
    const response = await axiosInstance.get(`/traders/groups/${groupId}/traders`, { params });
    return response.data;
  },

  getTraderGroupTelegramChats: async (groupId: string) => {
    const response = await axiosInstance.get(`/traders/groups/${groupId}/telegram-chats`);
    return response.data;
  },

  createTraderGroupTelegramChat: async (
    groupId: string,
    data: { chat_kind: string; title: string; invite_url: string },
  ) => (await axiosInstance.post(`/traders/groups/${groupId}/telegram-chats`, data)).data,

  updateTraderGroupTelegramChat: async (
    groupId: string,
    chatId: string,
    data: { chat_kind: string; title: string; invite_url: string },
  ) => (await axiosInstance.put(`/traders/groups/${groupId}/telegram-chats/${chatId}`, data)).data,

  createTrader: async (data: any) => {
    const response = await axiosInstance.post('/traders', data);
    return response.data;
  },

  updateTrader: async (id: string, data: any) => {
    const response = await axiosInstance.put(`/traders/${id}`, data);
    return response.data;
  },

  getTraderRates: async () => {
    try {
      const response = await axiosInstance.get('/traders/rates/');
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  },

  createTraderRate: async (data: any) => {
    const response = await axiosInstance.post('/traders/rates', data);
    return response.data;
  },

  updateTraderRate: async (id: string, data: any) => {
    const response = await axiosInstance.put(`/traders/rates/${id}`, data);
    return response.data;
  },

  getTraderRateAgents: async (rateId: string) => {
    const response = await axiosInstance.get(`/traders/rates/${rateId}/agents`);
    return response.data;
  },

  createTraderRateAgent: async (rateId: string, data: any) => {
    const response = await axiosInstance.post(`/traders/rates/${rateId}/agents`, data);
    return response.data;
  },

  updateTraderRateAgent: async (rateId: string, agentId: string, data: any) => {
    const response = await axiosInstance.put(`/traders/rates/${rateId}/agents/${agentId}`, data);
    return response.data;
  },

  deleteTraderRateAgent: async (rateId: string, agentId: string) => {
    const response = await axiosInstance.delete(`/traders/rates/${rateId}/agents/${agentId}`);
    return response.data;
  },

  deleteTraderRate: async (id: string) => {
    const response = await axiosInstance.delete(`/traders/rates/${id}`);
    return response.data;
  },

  getTraderBalances: async () => {
    try {
      const response = await axiosInstance.get('/traders/balances/');
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  },

  createTraderBalance: async (data: any) => {
    const response = await axiosInstance.post('/traders/balances', data);
    return response.data;
  },

  updateTraderBalance: async (id: string, data: any) => {
    const requestData: Record<string, number | string> = {
      available_balance: Number(data.available_balance) || 0,
      insurance_balance_usdt: Number(data.insurance_balance_usdt) || 0,
      blocked_balance_in: Number(data.blocked_balance_in) || 0,
      blocked_balance_out: Number(data.blocked_balance_out) || 0,
    };
    if (data.trc_twenty_wallet !== undefined) {
      requestData.trc_twenty_wallet = data.trc_twenty_wallet || '';
    }
    const response = await axiosInstance.put(`/traders/balances/${id}`, requestData);
    return response.data;
  },

  deleteTraderBalance: async (id: string) => {
    const response = await axiosInstance.delete(`/traders/balances/${id}`);
    return response.data;
  },

  addFundsToTraderBalance: async (
    id: string,
    payload: { amount: number; description?: string },
  ) => {
    const response = await axiosInstance.put(`/traders/balances/${id}/add-funds`, payload);
    return response.data;
  },

  withdrawFundsFromTraderBalance: async (
    id: string,
    payload: { amount: number; description?: string },
  ) => {
    const response = await axiosInstance.put(
      `/traders/balances/${id}/withdraw-funds`,
      payload,
    );
    return response.data;
  },

  exportTraderBalancesHistory: async (params: TraderBalancesHistoryExportParams) => {
    const response = await axiosInstance.get('/traders/balances/history/export', {
      params,
      responseType: 'blob',
    });
    return response;
  },

  getTraderBalancesHistory: async (params: TraderBalancesHistoryParams = {}) => {
    const response = await axiosInstance.get('/traders/balances/history', {
      params: {
        limit: 20,
        offset: 0,
        ...params,
      },
    });
    return response.data;
  },

  getTraderGroups: async () => {
    try {
      const response = await axiosInstance.get('/traders/groups/', {
        params: {
          page: 1,
          limit: DEFAULT_LIST_LIMIT,
        }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  },

  createTraderGroup: async (data: { name: string; owner_id: string }) => {
    const response = await axiosInstance.post('/traders/groups', data);
    return response.data;
  },

  updateTraderGroup: async (id: string, data: { name?: string; owner_id?: string }) => {
    const response = await axiosInstance.put(`/traders/groups/${id}`, data);
    return response.data;
  },

  deleteTraderGroup: async (id: string) => {
    const response = await axiosInstance.delete(`/traders/groups/${id}`);
    return response.data;
  },

  getTraderGroupReferralReport: async (params: TraderGroupReferralReportParams = {}) => {
    const response = await axiosInstance.get('/traders/groups/referral-report', { params });
    return response.data;
  },

  exportTraderGroupReferralReport: async (params: TraderGroupReferralReportExportParams) => {
    const response = await axiosInstance.get('/traders/groups/referral-report/export', {
      params,
      responseType: 'blob',
    });
    return response;
  },

  toggleTraderActive: async (id: string) => {
    const response = await axiosInstance.patch(`/traders/${id}/toggle-active`);
    return response.data;
  },

  /** Переключает флаг is_deled (мягкое удаление / восстановление). */
  toggleTraderIsDeled: async (id: string) => {
    const response = await axiosInstance.patch(`/traders/${id}/toggle-is-deled`);
    return response.data;
  },

  toggleTraderAutostop: async (id: string) => {
    const response = await axiosInstance.patch(`/traders/${id}/toggle-autostop`);
    return response.data;
  },

  toggleTraderRateActive: async (id: string) => {
    const response = await axiosInstance.patch(`/traders/rates/${id}/toggle-active`);
    return response.data;
  },
}; 
